import { useCallback } from 'react';
import { useCurrentProject } from '@/lib/current-project';

/**
 * Where project pages live (docs/first-slice-plan.md §6). A task opens over its project's Board at
 * `/p/<slug>/board/<taskId>` (the task's id, not its number), the URL the API's task links use too.
 */
export type ProjectSection = 'overview' | 'board' | 'runs' | 'settings';

export const projectPath = (slug: string, section: ProjectSection = 'board') => `/p/${slug}/${section}`;

/** A project's home, its Board (SF-T-189): where links to the project itself go. */
export const projectHomePath = (slug: string) => projectPath(slug, 'board');

export const runPath = (slug: string, number: number) => `/p/${slug}/runs/${number}`;

export const taskPath = (slug: string, taskId: string) => `/p/${slug}/board/${taskId}`;

/** The project slug in a path (`/p/<slug>/…`), or null outside a project. */
export function projectSlugFromPath(pathname: string): string | null {
  const match = /^\/p\/([^/]+)(?:\/|$)/.exec(pathname);
  return match ? decodeURIComponent(match[1]) : null;
}

/** Whether a path is the Board (with or without a task open over it). */
export function isBoardPath(pathname: string): boolean {
  return /^\/p\/[^/]+\/board(?:\/|$)/.test(pathname);
}

/**
 * The current project's Board, or a task on it, plus an optional query (`?tag=x`). Outside a
 * project (never for the project's own components) it points at the projects list.
 */
export function useBoardPath(): (taskId?: string | null, search?: string) => string {
  const slug = useCurrentProject()?.slug;
  return useCallback(
    (taskId?: string | null, search = '') => {
      if (!slug) return '/projects';
      return `${taskId ? taskPath(slug, taskId) : projectPath(slug)}${search}`;
    },
    [slug]
  );
}
